import { useQuery } from '@apollo/client'
import { useEffect, useMemo } from 'react'
import useAuthClaims from '../../../hooks/useAuthClaims'
import GetCountries from '../../../graphql/queries/GetCountries'
import { useAppDispatch, useAppSelector } from '../../../store'
import {
  selectSelectedCountryId,
  setSelectedCountryId,
} from '../slices/countryFilterSlice'
import CountryFlagSelect, {
  CountryFlagSelectItem,
} from '../views/CountryFlagSelect'

// Only visible to admins/librarians, who can switch which country's content they are viewing.
const CountryFilterContainer = () => {
  const { claims } = useAuthClaims()
  const isLibrarianOrAdmin = ['librarian', 'hlo-admin'].includes(
    claims?.role ?? '',
  )
  const dispatch = useAppDispatch()
  const selectedCountryId = useAppSelector(selectSelectedCountryId)

  const { data } = useQuery(GetCountries, {
    skip: !isLibrarianOrAdmin,
  })

  const countries: CountryFlagSelectItem[] = useMemo(
    () => data?.country || [],
    [data],
  )

  // Default to Denmark when browsed from the MBO domain, otherwise English/USA.
  // This only resolves the filter's own state, it never touches the site language.
  useEffect(() => {
    if (selectedCountryId || countries.length === 0) return
    if (typeof window === 'undefined') return

    const vercelUrl = process.env.NEXT_PUBLIC_VERCEL_URL
    const mboUrl = process.env.NEXT_PUBLIC_VERCEL_URL_MBO

    // Locally both env vars point at the same localhost, so fall back to a /da subroute check instead.
    const isMboHost =
      vercelUrl && vercelUrl === mboUrl
        ? /^\/da(\/|$)/.test(window.location.pathname)
        : !!mboUrl?.replace(/^https?:\/\//, '') &&
          window.location.host.includes(mboUrl.replace(/^https?:\/\//, ''))

    const defaultCountry =
      countries.find(
        ({ isocode }) => (isocode.toUpperCase() === 'DK') === isMboHost,
      ) || countries[0]

    dispatch(setSelectedCountryId(defaultCountry.id))
  }, [countries, dispatch, selectedCountryId])

  if (!isLibrarianOrAdmin) {
    return null
  }

  const selected = countries.find(({ id }) => id === selectedCountryId)

  return (
    <CountryFlagSelect
      countries={countries}
      selected={selected}
      onSelect={(item) => dispatch(setSelectedCountryId(item.id))}
    />
  )
}

export default CountryFilterContainer
